import React, { useState, useEffect } from 'react';
import { Box, Typography, Alert, Paper, Chip } from '@mui/material';
import { EmojiEvents as TrophyIcon, School as SchoolIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { studentAPI } from '../../services/api';
import GradeTable from './GradeTable';
import Loading from '../common/Loading';

// Groups the flat modules[] from getRattrapageResults into the nested
// { year: { '2': { 'S{n}': [...] } } } shape that GradeTable expects.
const buildRattrapageGrades = (modules, academicYear) => {
  if (!modules?.length || !academicYear) return null;
  const yearData = { '2': {} };
  modules.forEach(m => {
    const semKey = `S${m.semester_number || 0}`;
    if (!yearData['2'][semKey]) yearData['2'][semKey] = [];
    yearData['2'][semKey].push(m);
  });
  return { [academicYear]: yearData };
};

const CurrentSessionGrades = () => {
  const [rattrapageGrades, setRattrapageGrades]     = useState(null);
  const [isLoading, setIsLoading]                   = useState(true);
  const [error, setError]                           = useState(null);
  const [hasArabicNames, setHasArabicNames]         = useState(false);
  const [activeSession, setActiveSession]           = useState(null);
  const [reclaimableModules, setReclaimableModules] = useState(new Set());
  const [myReclamations, setMyReclamations]         = useState([]);
  const [rattrapageReclamationsEnabled, setRattrapageReclamationsEnabled] = useState(false);
  const [currentYear, setCurrentYear]               = useState(null);

  const { t } = useTranslation();

  const loadReclamations = async () => {
    try {
      const res = await studentAPI.getReclamations({ type: 'grade_reclamation' });
      setMyReclamations(res.reclamations || []);
    } catch (_) {}
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const [gradesResp, pedagoResp, reclaimable, reclSettings, rattrapageResp] = await Promise.all([
          studentAPI.getGrades({}),
          studentAPI.getPedagogicalSituation(),
          studentAPI.getReclaimableGrades(),
          studentAPI.getReclamationsSettings().catch(() => ({ enabled: true })),
          studentAPI.getRattrapageResults().catch(() => ({ session: null, modules: [] })),
        ]);

        const pedaYears = pedagoResp.available_years || [];
        const pedaYear  = pedaYears.length > 0 ? String(pedaYears[0]) : null;
        setCurrentYear(pedaYear);

        setHasArabicNames(gradesResp.has_arabic_names);

        // Reclamation eligibility always follows whichever session is currently
        // active (could be NORMALE or RATTRAPAGE) — drives the complaint button.
        setActiveSession(reclaimable.session || null);
        setReclaimableModules(new Set((reclaimable.modules || []).map(c => c.trim().toUpperCase())));

        setRattrapageReclamationsEnabled(reclSettings.rattrapageEnabled === true);

        const rattrapageYear = rattrapageResp.session?.academic_year || pedaYear;
        setRattrapageGrades(
          buildRattrapageGrades(rattrapageResp.modules || [], rattrapageYear)
        );

        await loadReclamations();
      } catch (err) {
        setError(err.response?.data?.error || t('grades_errorLoad'));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  if (isLoading) return <Loading message={t('grades_loading')} />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* Header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1565c0 60%, #1976d2 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      }}>
        <Box sx={{
          width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <TrophyIcon sx={{ fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{t('examResults')}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>{t('examResults_subtitle')}</Typography>
        </Box>
        {currentYear && (
          <Chip
            label={`${currentYear} — ${parseInt(currentYear) + 1}`}
            sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700, fontSize: '0.85rem' }}
          />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!rattrapageGrades && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <TrophyIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            {t('examResults_noResults')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('examResults_tryLater')}
          </Typography>
        </Paper>
      )}

      {/* ── Rattrapage results (shown first when available) ── */}
      {rattrapageGrades && (
        <>
          <Box sx={{
            background: 'linear-gradient(135deg, #78350f 0%, #b45309 60%, #d97706 100%)',
            borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}>
            <Box sx={{
              width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <SchoolIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700} lineHeight={1.2}>{t('rattrapageResults_title')}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>{t('rattrapageResults_subtitle')}</Typography>
            </Box>
            {currentYear && (
              <Chip
                label={`${currentYear} — ${parseInt(currentYear) + 1}`}
                sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: 'white', fontWeight: 700, fontSize: '0.85rem' }}
              />
            )}
          </Box>

          <GradeTable
            gradesData={rattrapageGrades}
            hasArabicNames={hasArabicNames}
            hideResult
            activeSession={activeSession}
            reclaimableModules={reclaimableModules}
            myReclamations={myReclamations}
            onReclamationSubmitted={loadReclamations}
            reclamationsEnabled={rattrapageReclamationsEnabled}
          />
        </>
      )}
    </Box>
  );
};

export default CurrentSessionGrades;
